import { useTranslation } from "react-i18next";

import {
  DETAIL_FIELDS,
  KRA_COLOURS,
  PERFORMANCE_REVIEW_TEXT,
  hasProposed,
  weightingTotal,
  type JDContent,
} from "../../utils/jobDescription";

/** A job description as people read it in PME People. The same structure as
 *  the PDF, with the summary table and the detail for each area combined. */
export default function JobDescriptionView({ content, compact = false }: { content: JDContent; compact?: boolean }) {
  const { t } = useTranslation();
  const kras = content.kras;
  const total = weightingTotal(content);
  const details = DETAIL_FIELDS.filter((f) => content.details[f.key]?.trim() || !f.optional);

  return (
    <div className="space-y-5 text-sm text-gray-800">
      <dl className="grid grid-cols-1 overflow-hidden rounded-xl border border-gray-200 sm:grid-cols-2">
        {details.map((f) => (
          <div key={f.key} className="border-b border-gray-100 px-3 py-2 sm:odd:border-r">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {t(`jd.field.${f.key}`, f.label)}
            </dt>
            <dd className="mt-0.5 break-words text-sm text-gray-900">{content.details[f.key] || "—"}</dd>
          </div>
        ))}
      </dl>

      <Section n={1} title={t("jd.view.purpose", "Job purpose")}>
        <p className="whitespace-pre-wrap leading-relaxed">{content.purpose || "—"}</p>
      </Section>

      <Section n={2} title={t("jd.view.kras", "Key result areas")}>
        {kras.length > 0 && total > 0 && (
          <div className="mb-3">
            <div className="flex h-2.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
              {kras.map((k, i) => (
                <span key={i} style={{ width: `${Math.min(k.weighting ?? 0, 100)}%`, background: KRA_COLOURS[i % KRA_COLOURS.length] }} />
              ))}
            </div>
            <p className="mt-1 text-[11px] text-gray-500">
              {t("jd.view.weightingNote", "How the role's time and results are weighted ({{total}}% in total).", { total })}
            </p>
          </div>
        )}
        <ol className="space-y-3">
          {kras.map((k, i) => (
            <li key={i} className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex items-start gap-2">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: KRA_COLOURS[i % KRA_COLOURS.length] }} aria-hidden />
                <p className="min-w-0 flex-1 font-semibold text-pme-navy">
                  2.{i + 1} {k.area || "—"}
                </p>
                {k.weighting != null && (
                  <span className="shrink-0 rounded-full bg-pme-sky px-2 py-0.5 text-xs font-bold text-pme-navy">{k.weighting}%</span>
                )}
              </div>
              {k.covers && <p className="mt-1 text-gray-700">{k.covers}</p>}
              {!compact && k.tasks.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500">{t("jd.view.tasks", "Tasks")}</p>
                  <ul className="mt-0.5 list-disc space-y-0.5 pl-5">
                    {k.tasks.map((task, j) => <li key={j}>{task}</li>)}
                  </ul>
                </div>
              )}
              {(k.measures.length > 0 || k.measured_by) && (
                <div className="mt-2">
                  <p className="text-xs font-semibold text-gray-500">{t("jd.view.measuredBy", "Measured by")}</p>
                  {k.measures.length > 0 ? (
                    <ul className="mt-0.5 list-disc space-y-0.5 pl-5">
                      {k.measures.map((m, j) => (
                        <li key={j}>
                          {m.text}
                          {m.proposed && (
                            <span className="ml-1 font-semibold text-amber-700" title={t("jd.view.proposedTitle", "Proposed, not agreed yet")}>*</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-0.5">{k.measured_by}</p>
                  )}
                </div>
              )}
            </li>
          ))}
          <li className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
            <div className="flex items-start gap-2">
              <p className="min-w-0 flex-1 font-semibold text-pme-navy">{t("jd.view.communication", "Communication & responsiveness")}</p>
              <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-gray-500">{t("jd.view.notWeighted", "Not weighted")}</span>
            </div>
            {content.communication.covers && <p className="mt-1 text-gray-700">{content.communication.covers}</p>}
            {content.communication.measured_by && (
              <p className="mt-1 text-xs text-gray-500">
                {t("jd.view.measuredBy", "Measured by")}: {content.communication.measured_by}
              </p>
            )}
          </li>
        </ol>
        {hasProposed(content) && (
          <p className="mt-2 text-xs text-amber-800">
            * {content.proposed_note || t("jd.view.proposedNote", "Proposed measure, not agreed yet.")}
          </p>
        )}
      </Section>

      {content.competencies.length > 0 && (
        <Section n={3} title={t("jd.view.competencies", "Behavioural competencies")}>
          <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
            {content.competencies.map((c, i) => (
              <li key={i} className="px-3 py-2 sm:flex sm:gap-4">
                <p className="font-semibold text-gray-900 sm:w-56 sm:shrink-0">{c.name}</p>
                <p className="text-gray-700">{c.description}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {!compact && (
        <Section n={content.competencies.length > 0 ? 4 : 3} title={t("jd.view.review", "Performance review")}>
          <p className="leading-relaxed text-gray-700">{t("jd.view.reviewText", PERFORMANCE_REVIEW_TEXT)}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 border-b-2 border-pme-blue pb-1 text-base font-bold text-pme-navy">
        {n}. {title}
      </h3>
      {children}
    </section>
  );
}
